import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ExtendJob, JobExtended } from "../Utils/TypeExtensions";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { getJobList } from "../sharedCode/queries";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import { Job } from "../sharedCode/avroGraphql";
import dayjs from "dayjs";

export default function useJobBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getJobList, { fetchPolicy: "no-cache" });

    const fetchData = (firstRun = false) => {
        const startDate = dayjs().subtract(30, "days").format("YYYY-MM-DD");
        if (firstRun) {
            doQuery({ variables: { startDate } });
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch({ startDate })
                .then((results) => {
                    const rawList = results.data.getJobs.slice() as Job[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    async function saveToLocalDatabase(rawList: Job[]) {
        const listToSave = ObjectUtils.convertToUppercase(rawList.map((job) => ExtendJob(job, rawList))) as JobExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.job, async () => {
            await TicketDatabase.job?.clear().then(() => TicketDatabase.job.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getJobs.slice() as Job[];
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.job.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
